import React, { useState, useEffect } from "react";
import { Navigate } from "react-router-dom";
import { ActionButton, Input } from "../components/shared";
import { APP_NAME } from "../constants/branding";
import { authService } from "../services";
import background from "../assets/images/financial_bg.jpg";
import logoPotenciaLucrativa from "../assets/images/logo-potencia-lucrativa.png";

interface LoginFormData {
  email: string;
  password: string;
}

export const LoginPage: React.FC = () => {
  const [formData, setFormData] = useState<LoginFormData>({
    email: "",
    password: "",
  });
  const [errors, setErrors] = useState<Partial<LoginFormData>>({});
  const [loginError, setLoginError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);

  // Load saved email on component mount
  useEffect(() => {
    const savedEmail = localStorage.getItem("remembered_email");
    if (savedEmail) {
      setFormData((prev) => ({ ...prev, email: savedEmail }));
      setRememberMe(true);
    }
  }, []);

  // Redirect if already authenticated (after hooks)
  if (authService.isAuthenticated()) {
    return <Navigate to="/transactions" replace />;
  }

  const handleAdminLogin = async (loginData: LoginFormData): Promise<void> => {
    try {
      const response = await authService.login(loginData);

      if (response.success && response.data) {
        // Save email if remember me is checked
        if (rememberMe) {
          localStorage.setItem("remembered_email", loginData.email);
        } else {
          localStorage.removeItem("remembered_email");
        }

        // Redirect to admin dashboard
        window.location.href = `/transactions`;
      } else {
        throw new Error(response.error || "Login failed");
      }
    } catch (error) {
      console.error("Login failed:", error);
      throw error;
    }
  };

  const handleInputChange = (field: keyof LoginFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<LoginFormData> = {};

    if (!formData.email.trim()) {
      newErrors.email = "Email é obrigatório";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Email inválido";
    }

    if (!formData.password.trim()) {
      newErrors.password = "Senha é obrigatória";
    } else if (formData.password.length < 4) {
      newErrors.password = "Senha deve ter pelo menos 4 caracteres";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setLoginError(null);

    try {
      await handleAdminLogin(formData);
    } catch (error: any) {
      setLoginError(error.message || "Login failed. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4 relative"
      style={{
        backgroundImage: `url(${background})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}
    >
      {/* Dark overlay for better contrast */}
      <div className="absolute inset-0"></div>

      {/* Login Card */}
      <div className="relative w-full max-w-md z-10">
        <div className="absolute inset-0 bg-gradient-to-r from-gray-600 to-gray-800 rounded-3xl blur opacity-20 animate-pulse"></div>

        <div className="relative bg-gray-900/80 backdrop-blur-xl rounded-3xl shadow-2xl border border-gray-600/30 overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-gray-800 to-gray-900 px-8 py-6 text-center">
            <div className="flex justify-center mb-5">
              <img
                src={logoPotenciaLucrativa}
                alt={`Logotipo ${APP_NAME}`}
                className="h-44 sm:h-52 w-auto max-w-full object-contain object-center drop-shadow-md"
              />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">
              Seja bem-vindo!
            </h1>
            <p className="text-gray-300">Entre na sua conta para continuar</p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-8 space-y-6 bg-gray-800">
            {/* Email Input */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-200">
                Email
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <span className="text-gray-400 text-lg">📧</span>
                </div>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange("email", e.target.value)}
                  placeholder="seu@email.com"
                  className={`pl-12 !bg-[#374151] !text-slate-100 !placeholder-slate-400 !border-[#4B5563] focus:!border-gray-400 focus:!ring-gray-500 focus:!ring-2 ${
                    errors.email
                      ? "!border-red-400 focus:!border-red-500 focus:!ring-red-300"
                      : ""
                  }`}
                />
              </div>
              {errors.email && (
                <p className="text-sm text-red-600 flex items-center gap-1">
                  <span>⚠️</span>
                  {errors.email}
                </p>
              )}
            </div>

            {/* Password Input */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-200">
                Senha
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <span className="text-gray-400 text-lg">🔒</span>
                </div>
                <Input
                  type="password"
                  value={formData.password}
                  onChange={(e) =>
                    handleInputChange("password", e.target.value)
                  }
                  placeholder="••••••••"
                  className={`pl-12 !bg-[#374151] !text-slate-100 !placeholder-slate-400 !border-[#4B5563] focus:!border-gray-400 focus:!ring-gray-500 focus:!ring-2 ${
                    errors.password
                      ? "!border-red-400 focus:!border-red-500 focus:!ring-red-300"
                      : ""
                  }`}
                />
              </div>
              {errors.password && (
                <p className="text-sm text-red-600 flex items-center gap-1">
                  <span>⚠️</span>
                  {errors.password}
                </p>
              )}
            </div>

            {/* Remember Me */}
            <div className="flex items-center justify-between">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="w-4 h-4 text-gray-600 bg-gray-700 border-gray-600 rounded focus:ring-gray-500 focus:ring-2"
                />
                <span className="ml-2 text-sm text-gray-300">
                  Lembrar de mim
                </span>
              </label>
              <button
                type="button"
                className="text-sm text-gray-400 hover:text-gray-200 font-medium transition-colors duration-200"
              >
                Esqueci a senha
              </button>
            </div>

            {/* Login Error */}
            {loginError && (
              <div className="bg-red-900/50 border border-red-500 rounded-lg p-4">
                <p className="text-red-200 text-sm flex items-center gap-2">
                  <span>⚠️</span>
                  {loginError}
                </p>
              </div>
            )}

            {/* Login Button */}
            <ActionButton
              type="submit"
              variant="primary"
              size="lg"
              loading={isSubmitting}
              loadingLabel="Entrando..."
              className="w-full py-4 font-bold text-lg justify-center"
            >
              {!isSubmitting && (
                <div className="flex items-center justify-center gap-3">
                  <span className="text-xl">🚀</span>
                  <span>Entrar</span>
                </div>
              )}
              {isSubmitting && "Entrando..."}
            </ActionButton>
          </form>
        </div>
      </div>

      {/* Footer */}
      <div className="absolute bottom-4 text-center text-gray-400 text-sm">
        <p>© 2026 {APP_NAME}. Todos os direitos reservados.</p>
      </div>
    </div>
  );
};
